import * as utils from '@/common/utils'
import * as googleMaps from '@/common/googleMaps'

export default {}

let defListeners = []

/**
 * Translates a lat/lng to a pixel position
 *
 * @param map google.maps.Map
 * @param position google.maps.LatLng
 *
 * @returns google.maps.Point
 *
 * @from https://stackoverflow.com/questions/1538681/how-to-call-fromlatlngtodivpixel-in-google-maps-api-v3
 */
export const fromLatLngToPixel = (map, position) => {
  const scale = Math.pow(2, map.getZoom())
  const proj = map.getProjection()
  const bounds = map.getBounds()

  const nw = proj.fromLatLngToPoint(
    new google.maps.LatLng(
      bounds.getNorthEast().lat(),
      bounds.getSouthWest().lng()
    ))
  const point = proj.fromLatLngToPoint(position)

  return new google.maps.Point(
    Math.floor((point.x - nw.x) * scale),
    Math.floor((point.y - nw.y) * scale))
}

/**
 * Translates a pixel position to a lat/lng position.
 *
 * @param map google.maps.Map
 * @param pixel google.maps.Point
 *
 * @returns google.maps.Point
 *
 * @from https://stackoverflow.com/questions/1538681/how-to-call-fromlatlngtodivpixel-in-google-maps-api-v3
 */
export const fromPixeltoLatLng = (map, pixel) => {
  const scale = Math.pow(2, Map.getZoom())
  const proj = Map.getProjection()
  const bounds = Map.getBounds()

  const nw = proj.fromLatLngToPoint(
    new google.maps.LatLng(
      bounds.getNorthEast().lat(),
      bounds.getSouthWest().lng()
    ))
  const point = new google.maps.Point()

  point.x = pixel.x / scale + nw.x
  point.y = pixel.y / scale + nw.y

  return proj.fromPointToLatLng(point)
}

/**
 * Determines whether the specified position is within a certain percentage
 * (default 10%) from the map edge.
 *
 * @param mapContainer  Element The map's container element
 * @param position     google.maps.LatLng
 * @param distanceLimit Number
 */
export const isNearMapEdge = (mapContainer, position, distanceLimit = 0.1) => {
  const x1 = mapContainer.offsetWidth * distanceLimit
  const y1 = mapContainer.offsetHeight * distanceLimit
  const x2 = mapContainer.offsetWidth - x1
  const y2 = mapContainer.offsetHeight - y1
  const point = fromLatLngToPixel(googleMaps.map, position)

  return (
    point.x < x1 ||
    point.y < y1 ||
    point.x > x2 ||
    point.y > y2
  )
}

/**
 * Creates a label on the map at a specific lat,lng position. The label's
 * contents are stored in a div that is injected into the specified container.
 *
 * Options is an object that may have the following keys:
 * - labelClass (string): A css class to use for the label
 * - offsetX (int): Adjusts the X (horizontal) position of the label positively or negatively.
 * - offsetY (int): Adjusts the Y (vertical) position of the label positively or negatively.
 * - container (string): The DOM id of the container for labels.
 *
 * Returns an object containing all the original label properties along with a uuid string
 * that can be used to track the injected div/label.
 *
 * @param map google.maps.Map
 * @param position google.maps.LatLng
 * @param content HTML or text to put in the label.
 * @param marker google.maps.Marker|null
 * @param options Object
 * @param onClick null|function
 *
 * @return MapLabel
 */
export const createLabel = (map, position, content, marker = null, options = {}, onClick = null) => {
  options = Object.assign({
    container: 'map-injections'
  }, options)

  const uuid = utils.uuid()

  let container = document.getElementById(options.container)
  container.innerHTML = container.innerHTML +
    `<div id="${uuid}">${content}</div>`

  let el = document.getElementById(uuid)

  let MapLabel = createLabelClass()
  let label = new MapLabel(uuid, position, el, options, marker)
  label.setMap(map)

  if (onClick !== null) {
    google.maps.event.addListener(label, 'click', onClick)
  }

  return label
}

/**
 * Creates a label on the map at a specific lat,lng position. The label's
 * contents are stored in a div that is injected into the specified container.
 *
 * Options is an object that may have the following keys:
 * - labelClass (string): A css class to use for the label
 * - offsetX (int): Adjusts the X (horizontal) position of the label positively or negatively.
 * - offsetY (int): Adjusts the Y (vertical) position of the label positively or negatively.
 * - container (string): The DOM id of the container for labels.
 *
 * Returns an object containing all the original label properties along with a uuid string
 * that can be used to track the injected div/label.
 *
 * @param map google.maps.Map
 * @param position google.maps.LatLng
 * @param content HTML or text to put in the label.
 * @param marker google.maps.Marker
 * @param options Object
 *
 * @return MapLabel
 */
export const createMarkerLabel = (map, position, content, marker, options = {}) => {
  options = Object.assign({
    container: 'map-injections'
  }, options)

  const uuid = utils.uuid()

  let container = document.getElementById(options.container)
  container.innerHTML = container.innerHTML +
    `<div id="${uuid}">${content}</div>`

  let el = document.getElementById(uuid)

  let MapLabel = createLabelClass()
  let label = new MapLabel(uuid, position, el, options, marker)
  label.setMap(map)

  return label
}

/**
 * Updates the content of a marker label.
 *
 * @param marker google.maps.Marker
 * @param content HTML or text to put in the label.
 */
export const updateMarkerLabel = (marker, content) => {
  let el = document.getElementById(marker.markerLabel.uuid)
  if (typeof el !== 'undefined') {
    el.innerHTML = content
  }
}

/**
 * Hides the content of a marker label by settings its display to none
 *
 * @param marker google.maps.Marker
 */
export const hideMarkerLabel = (marker) => {
  let el = document.getElementById(marker.markerLabel.uuid)
  if (el && el.parentElement) {
    el.parentElement.style.display = 'none'
  }
}

/**
 * Shows the content of a marker label by setting its display to block
 *
 * @param marker google.maps.Marker
 */
export const showMarkerLabel = (marker) => {
  let el = document.getElementById(marker.markerLabel.uuid)
  if (el && el.parentElement) {
    el.parentElement.style.display = 'block'
  }
}

/**
 * Calculates the text width of an element by temporarily
 * injecting an element into the container to get the width
 * and then it deletes the temporary element again.
 *
 * @param text string
 * @param container string
 * @param uuid string
 * @returns {number}
 */
export const getTextWidth = (text, container, uuid) => {
  let textEl = document.createElement('div')
  textEl.id = uuid
  textEl.className = 'el'
  textEl.innerHTML = text
  textEl.style.position = 'absolute'
  textEl.style.whiteSpace = 'nowrap'
  textEl.style.font = 'inherit'

  let containerEl = document.getElementById(container)
  containerEl.appendChild(textEl)

  let tmpEl = document.getElementById(uuid)
  let width = tmpEl.clientWidth
  tmpEl.remove()
  return width
}

/**
 * Add's default listeners to the map for tracking things like boundary changes
 * in order to update all labels on the map, etc.
 *
 * @param map google.maps.Map
 */
export const addDefaultListeners = (map) => {
  /*
  const listener = google.maps.event.addListener(map, 'bounds_changed', () => {
  })

  defListeners.push(listener)
  */
}

/**
 * Removes all default listeners.
 */
export const removeDefaultListeners = () => {
  defListeners.forEach((listener) => {
    google.maps.event.removeListener(listener)
  })

  defListeners = []
}

/**
 * Converts a decimal based latitude and longitude to a google.maps.LatLng
 * object.
 *
 * @param lat Number
 * @param lng Number
 *
 * @returns google.maps.LatLng|null
 */
export const latLng = (lat, lng) => {
  return new google.maps.LatLng(lat, lng)
}

/**
 * Converts a string based latitude and longitude to a google.maps.LatLng
 * object, or null if it failed.
 *
 * @param latStr String
 * @param lngStr String
 *
 * @returns google.maps.LatLng|null
 */
export const strToLatLng = (latStr, lngStr) => {
  if (latStr && lngStr) {
    let lat = NaN
    let lng = NaN

    // First try and determine whether the user entered decimal coordinates or
    // DMS (degree, minute, second) coordinates.
    if (!isNaN(latStr)) {
      lat = parseFloat(latStr)
    } else {
      // Latitude is not a pure numeric number, lets try DMS format
      lat = dmsLatToGPS(latStr)
    }

    if (!isNaN(lngStr)) {
      lng = parseFloat(lngStr)
    } else {
      // Longitude is not a pure numeric number, lets try DMS format
      lng = dmsLngToGPS(lngStr)
    }

    if (!lng || !lat) {
      return null
    } else {
      return new google.maps.LatLng(lat, lng)
    }
  } else {
    return null
  }
}

/**
 * Converts a DMS formatted latitude as a decimal coordinate.
 *
 * @param coordinate String
 *
 * @returns Number
 */
export const dmsLatToGPS = (coordinate) => {
  // First get all the individual elements of the coordinate. We do this by
  // replacing all non-digit characters (except for N/S/E/W) with commas (,).
  // We then split the string by commas.
  let elements = coordinate.trim().toUpperCase().replace(/[^\dNS.-]/g, ',').split(',')
  let degrees = 0
  let minutes = 0
  let seconds = 0
  let direction = ''
  let result = NaN

  if (elements.length >= 4) {
    // Try and determine the direction (hemisphere)
    if (elements[0] === 'N' || elements[0] === 'S') {
      degrees = parseFloat(elements[1])
      minutes = parseFloat(elements[2])
      seconds = parseFloat(elements[3])
      direction = elements[0]
    } else if (elements[3] === 'N' || elements[3] === 'S') {
      degrees = parseFloat(elements[0])
      minutes = parseFloat(elements[1])
      seconds = parseFloat(elements[2])
      direction = elements[3]
    } else {
      // No direction supplied, see if the first value is a positive or negative number
      if (parseFloat(elements[0])) {
        degrees = parseFloat(elements[0])
        minutes = parseFloat(elements[1])
        seconds = parseFloat(elements[2])
        if (degrees && minutes && seconds) {
          if (degrees < 0) {
            direction = 'S'
          } else {
            direction = 'N'
          }
        }
      }
    }
  } else if (elements.length >= 3) {
    // We assume the direction (hemisphere was not supplied)
    if (parseFloat(elements[0])) {
      degrees = parseFloat(elements[0])
      minutes = parseFloat(elements[1])
      seconds = parseFloat(elements[2])
      if (degrees && minutes && seconds) {
        if (degrees < 0) {
          direction = 'S'
        } else {
          direction = 'N'
        }
      }
    }
  }

  if (degrees && minutes && seconds && direction) {
    result = degrees + minutes / 60 + seconds / 3600
    result = direction === 'S' ? result * -1 : result
  }

  return result
}

/**
 * Converts a DMS formatted longitude as a decimal coordinate.
 *
 * @param coordinate String
 *
 * @returns Number
 */
export const dmsLngToGPS = (coordinate) => {
  // First get all the individual elements of the coordinate. We do this by
  // replacing all non-digit characters (except for N/S/E/W) with commas (,).
  // We then split the string by commas.
  let elements = coordinate.trim().toUpperCase().replace(/[^\dEW.-]/g, ',').split(',')
  let degrees = 0
  let minutes = 0
  let seconds = 0
  let direction = ''
  let result = NaN

  if (elements.length >= 4) {
    // Try and determine the direction (hemisphere)
    if (elements[0] === 'E' || elements[0] === 'W') {
      degrees = parseFloat(elements[1])
      minutes = parseFloat(elements[2])
      seconds = parseFloat(elements[3])
      direction = elements[0]
    } else if (elements[3] === 'E' || elements[3] === 'W') {
      degrees = parseFloat(elements[0])
      minutes = parseFloat(elements[1])
      seconds = parseFloat(elements[2])
      direction = elements[3]
    } else {
      // No direction supplied, see if the first value is a positive or negative number
      if (parseFloat(elements[0])) {
        degrees = parseFloat(elements[0])
        minutes = parseFloat(elements[1])
        seconds = parseFloat(elements[2])
        if (degrees && minutes && seconds) {
          if (degrees < 0) {
            direction = 'W'
          } else {
            direction = 'E'
          }
        }
      }
    }
  } else if (elements.length >= 3) {
    // We assume the direction (hemisphere was not supplied)
    if (parseFloat(elements[0])) {
      degrees = parseFloat(elements[0])
      minutes = parseFloat(elements[1])
      seconds = parseFloat(elements[2])
      if (degrees && minutes && seconds) {
        if (degrees < 0) {
          direction = 'W'
        } else {
          direction = 'E'
        }
      }
    }
  }

  if (degrees && minutes && seconds && direction) {
    result = degrees + minutes / 60 + seconds / 3600
    result = direction === 'W' ? result * -1 : result
  }

  return result
}

/**
 * Converts a decimal coordinate to a DMS formatted coordinate.
 *
 * @param coordinate Number
 * @param formatType String ('dms' or 'decimal')
 * @param direction String
 *
 * @returns String
 */
export const gpsToDMS = (coordinate, formatType, direction) => {
  if (formatType === 'dms') {
    let deg = 0 | (coordinate < 0 ? coordinate = -coordinate : coordinate)
    let min = 0 | coordinate % 1 * 60
    let sec = Math.round((coordinate * 60 % 1 * 6000) / 100)

    return deg.toString() + '`' + min.toString() + '`' + sec.toString() + '"' + direction
  } else {
    return coordinate
  }
}

/**
 * Returns Decimal latitude as DMS (Degrees,Minutes,Seconds)
 *
 * @param lat Float
 * @param formatType String ('dms' or 'decimal')
 *
 * @returns String
 */
export const gpsLatToDMS = (lat, formatType) => {
  if (lat < 0) {
    return gpsToDMS(lat, formatType, 'S')
  } else {
    return gpsToDMS(lat, formatType, 'N')
  }
}

/**
 * Returns Decimal longitude as DMS (Degrees,Minutes,Seconds)
 *
 * @param lng Float
 * @param formatType String ('dms' or 'decimal')
 *
 * @returns String
 */
export const gpsLngToDMS = (lng, formatType) => {
  if (lng < 0) {
    return gpsToDMS(lng, formatType, 'W')
  } else {
    return gpsToDMS(lng, formatType, 'E')
  }
}

/**
 * Returns the MapLabel class.
 *
 * Unfortunately, the MapLabel class can only be defined after
 * google.maps.OverlayView is defined, when the Maps API is loaded.
 */
function createLabelClass () {
  /**
   * Creates a label on the map at a specific lat,lng position. The label's
   * contents are stored in a div that is injected into the specified container.
   *
   * Options is an object that may have the following keys:
   * - labelClass (string): A css class to use for the label
   * - offsetX (int): Adjusts the X (horizontal) position of the label positively or negatively.
   * - offsetY (int): Adjusts the Y (vertical) position of the label positively or negatively.
   * - container (string): The DOM id of the container for labels.
   *
   * Returns an object containing all the original label properties along with a uuid string
   * that can be used to track the injected div/label.
   *
   * @param uuid string
   * @param position google.maps.LatLng
   * @param content HTML or text to put in the label.
   * @param options Object
   * @param marker google.maps.Marker
   *
   * @return Object
   */
  function MapLabel (uuid, position, content, options = {}, marker = null) {
    options = Object.assign({
      labelClass: 'map-label',
      labelAnchorClass: 'map-label-anchor',
      labelContainerClass: 'map-label-container',
      addCss: '',
      offsetX: 0,
      offsetY: -20
    }, options)

    this.position = position
    this.options = options
    this.marker = marker
    this.el = content
    this.uuid = uuid
    if (this.marker !== null) {
      this.marker.markerLabel = this
    }

    content.classList.add(options.labelClass)
    if (options.addCss !== '') {
      content.classList.add(options.addCss)
    }

    // This zero-height div is positioned at the bottom of the label.
    this.anchor = document.createElement('div')
    this.anchor.classList.add(options.labelAnchorClass)
    if (options.addCss !== '') {
      this.anchor.classList.add(options.addCss)
    }
    this.anchor.appendChild(content)

    // This zero-height div is positioned at the bottom of the tip.
    this.containerDiv = document.createElement('div')
    this.containerDiv.classList.add(options.labelContainerClass)
    this.containerDiv.appendChild(this.anchor)

    // Stop clicks, etc., from bubbling up to the map.
    if (marker !== null) {
      google.maps.OverlayView.preventMapHitsAndGesturesFrom(this.containerDiv)
    }
  }

  // ES5 magic to extend google.maps.OverlayView.
  MapLabel.prototype = Object.create(google.maps.OverlayView.prototype)

  /** Called when the label is added to the map. */
  MapLabel.prototype.onAdd = function () {
    this.getPanes().floatPane.appendChild(this.containerDiv)

    // Add element to clickable layer
    this.getPanes().overlayMouseTarget.appendChild(this.containerDiv)

    // Set this as locally scoped var so event does not get confused
    let me = this

    google.maps.event.addDomListener(this.containerDiv, 'click', function () {
      // google.maps.event.trigger(me, 'click')
      // Pass the marker to the event handler instead of the label.
      // This is so that the event handler knows which marker
      // it is dealing with.
      if (typeof me.marker !== 'undefined') {
        google.maps.event.trigger(me.marker, 'click')
      } else {
        google.maps.event.trigger(null, 'click')
      }
    })
  }

  /** Called when the label is removed from the map. */
  MapLabel.prototype.onRemove = function () {
    if (this.containerDiv.parentElement) {
      this.containerDiv.parentElement.removeChild(this.containerDiv)
    }
  }

  /** Called each frame when the label needs to draw itself. */
  MapLabel.prototype.draw = function () {
    let divPosition = this.getProjection().fromLatLngToDivPixel(this.position)
    divPosition.x += this.options.offsetX
    divPosition.y += this.options.offsetY

    // Hide the label when it is far out of view.
    let display =
      Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000 ? 'block' : 'none'

    if (display === 'block') {
      this.containerDiv.style.left = divPosition.x + 'px'
      this.containerDiv.style.top = divPosition.y + 'px'
    }

    if (this.containerDiv.style.display !== display) {
      this.containerDiv.style.display = display
    }
  }

  return MapLabel
}

/**
 * Creates a label on the map at a specific lat,lng position. The label's
 * contents are stored in a div that is injected into the specified container.
 *
 * Options is an object that may have the following keys:
 * - labelClass (string): A css class to use for the label
 * - offsetX (int): Adjusts the X (horizontal) position of the label positively or negatively.
 * - offsetY (int): Adjusts the Y (vertical) position of the label positively or negatively.
 * - container (string): The DOM id of the container for labels.
 *
 * Returns an object containing all the original label properties along with a uuid string
 * that can be used to track the injected div/label.
 *
 * @param map google.maps.Map
 * @param position google.maps.LatLng
 * @param content HTML or text to put in the label.
 * @param data Data to be tagged onto the marker.
 * @param options Object
 * @param onClick null|function
 *
 * @return MapLabel
 */
export const createCustomMarker = (map, position, content, data = null, options = {}, onClick = null) => {
  options = Object.assign({
    container: 'map-injections'
  }, options)

  const uuid = utils.uuid()

  let container = document.getElementById(options.container)
  container.innerHTML = container.innerHTML +
    `<div id="${uuid}">${content}</div>`

  let el = document.getElementById(uuid)
  el.style.zIndex = '99'

  let CustomMarker = createCustomMarkerClass()
  let customMarker = new CustomMarker(uuid, position, el, data, options)
  customMarker.setMap(map)

  if (onClick !== null) {
    google.maps.event.addListener(customMarker, 'click', onClick)
  }

  return customMarker
}

export const removeCustomMarker = (customMarker) => {
  customMarker.setMap(null)
}

/**
 * Returns the MapLabel class.
 *
 * Unfortunately, the MapLabel class can only be defined after
 * google.maps.OverlayView is defined, when the Maps API is loaded.
 */
function createCustomMarkerClass () {
  /**
   * Creates a label on the map at a specific lat,lng position. The label's
   * contents are stored in a div that is injected into the specified container.
   *
   * Options is an object that may have the following keys:
   * - labelClass (string): A css class to use for the label
   * - offsetX (int): Adjusts the X (horizontal) position of the label positively or negatively.
   * - offsetY (int): Adjusts the Y (vertical) position of the label positively or negatively.
   * - container (string): The DOM id of the container for labels.
   *
   * Returns an object containing all the original label properties along with a uuid string
   * that can be used to track the injected div/label.
   *
   * @param uuid string
   * @param position google.maps.LatLng
   * @param content HTML or text to put in the label.
   * @param data Data to be tagged onto the marker.
   * @param options Object
   *
   * @return Object
   */
  function CustomMarker (uuid, position, content, data = null, options = {}) {
    options = Object.assign({
      labelClass: 'map-text-marker',
      labelContainerClass: 'map-text-marker-container',
      addCss: '',
      offsetX: 0,
      offsetY: 0
    }, options)

    this.position = position
    this.options = options
    this.el = content
    this.uuid = uuid
    this.data = data

    content.classList.add(options.labelClass)
    if (options.addCss !== '') {
      content.classList.add(options.addCss)
    }

    // This zero-height div is positioned at the bottom of the tip.
    this.containerDiv = document.createElement('div')
    this.containerDiv.classList.add(options.labelContainerClass)
    this.containerDiv.appendChild(content)

    // Stop clicks, etc., from bubbling up to the map.
    google.maps.OverlayView.preventMapHitsAndGesturesFrom(this.containerDiv)
  }

  // ES5 magic to extend google.maps.OverlayView.
  CustomMarker.prototype = Object.create(google.maps.OverlayView.prototype)

  /** Called when the label is added to the map. */
  CustomMarker.prototype.onAdd = function () {
    this.getPanes().floatPane.appendChild(this.containerDiv)

    // Add element to clickable layer
    this.getPanes().overlayMouseTarget.appendChild(this.containerDiv)

    // Set this as locally scoped var so event does not get confused
    let me = this

    google.maps.event.addDomListener(this.containerDiv, 'click', function () {
      google.maps.event.trigger(me, 'click')
    })
  }

  /** Called when the label is removed from the map. */
  CustomMarker.prototype.onRemove = function () {
    if (this.containerDiv.parentElement) {
      this.containerDiv.parentElement.removeChild(this.containerDiv)
    }
  }

  /** Called each frame when the label needs to draw itself. */
  CustomMarker.prototype.draw = function () {
    let divPosition = this.getProjection().fromLatLngToDivPixel(this.position)
    divPosition.x += this.options.offsetX
    divPosition.y += this.options.offsetY

    // Hide the label when it is far out of view.
    let display =
      Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000 ? 'block' : 'none'

    if (display === 'block') {
      this.containerDiv.style.left = divPosition.x + 'px'
      this.containerDiv.style.top = divPosition.y + 'px'
    }

    if (this.containerDiv.style.display !== display) {
      this.containerDiv.style.display = display
    }
  }

  return CustomMarker
}



// WEBPACK FOOTER //
// ./src/maps/google/mapUtils.js